import { PdfChart, PdfChartProps } from "@app/components/Pdf";
import { ChartConfiguration, FontSpec } from "chart.js";
import { ChartPlugins, ChartUtils } from "@app/utils/chartUtils";
import { useTheme } from "@mui/material";
import { Image as PdfImage, Text, View } from "@react-pdf/renderer";
import { formatPercentFactor } from "@app/utils";
import { ChartDataset } from "chart.js";
import { CarPortfolioChartItem } from "@app/pages/assetAllocation/asset-allocation-types";
import { getMultiPeriodReturnComparisonChartData } from "@app/pages/assetAllocation/editPortfolioGroup/ChartMultiPeriodReturnComparison";
import { CarPdfBox } from "@app/components/PdfBox";
import { CarPdfChartLegend } from "@app/components/PdfChartLegend";
import { CarPdfChart, CarPdfChartProps } from "@app/components/PdfChart";
import { CarGicsReport } from "@app/hooks/useGics";
import { getSectorSummaryChartData } from "@app/pages/assetAllocation/editPortfolioGroup/ChartSectorSummary";

interface SectorAnalysisPdfChartProps
  extends Omit<CarPdfChartProps, "type" | "options" | "data" | "plugins"> {
  data: CarPortfolioChartItem[];
  report: CarGicsReport;
  data1Id: string;
  data2Id: string;
  isWhiteContext?: boolean;
}

export const SectorAnalysisPdfChart = ({
  data,
  report,
  data1Id,
  data2Id,
  isWhiteContext,
  ...props
}: SectorAnalysisPdfChartProps) => {
  const theme = useTheme();

  const label1 = data.find((i) => i.id === data1Id)?.fullLabel ?? "";
  const label2 = data.find((i) => i.id === data2Id)?.fullLabel ?? "";

  const { datasets, yLabel, xLabels } = getSectorSummaryChartData({
    theme,
    data: report.level1,
    label1,
    label2,
    forPdf: true,
  });

  const font: Partial<FontSpec> = {
    size: 11,
    weight: 400,
    lineHeight: 1,
    family: theme.typography.fontFamily,
  };

  const titleFont: Partial<FontSpec> = {
    ...font,
    size: 12,
    weight: 600,
  };

  return (
    <CarPdfBox>
      <CarPdfChart
        type="bar"
        data={{ datasets }}
        options={{
          indexAxis: "y",
          scales: {
            x: {
              position: "top",
              ticks: {
                font,
                color: theme.palette.softBlack,
                padding: 0,
                callback: (value) =>
                  typeof value === "number"
                    ? formatPercentFactor(value)
                    : value,
              },
              grid: ChartUtils.getGridOptions({ theme }),
              title: {
                display: true,
                text: yLabel,
                color: theme.palette.softBlack,
                font: titleFont,
              },
            },
            y: {
              type: "category",
              ticks: {
                font,
                color: theme.palette.softBlack,
                padding: 0,
              },
              grid: ChartUtils.getGridOptions({ theme, showMinMaxLines: true }),
              labels: xLabels,
            },
          },
          plugins: {
            roundedBackground: ChartUtils.getRoundedBackgroundOptions({
              theme,
              isWhiteContext,
            }),
          },
        }}
        plugins={[ChartPlugins.roundedBackground]}
        {...props}
      />
      <CarPdfChartLegend
        style={{ marginTop: 16 }}
        items={datasets.map((i) => ({
          label: i.label ?? "",
          color: String(i.backgroundColor),
          isCircle: true,
        }))}
      />
    </CarPdfBox>
  );
};
